import { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, Navigate, useParams } from "react-router-dom";

import api from "../../services/api";
import { notify } from "../../components/ui/Toast";
import { readApiError } from "../../utils/apiError";
import { exactTime, relativeTime } from "../../utils/time";
import { Confirm } from "../../components/ui/panelKit";
import SeenPill, { unseen } from "../../components/ui/SeenPill";
import ThreadsTable from "../Threads/ThreadsTable";
import CredentialsPanel from "./CredentialsPanel";
import GroupsPanel, { toTree } from "./GroupsPanel";
import ManagersPanel from "./ManagersPanel";
import MembersPanel from "./MembersPanel";
import OriginsPanel from "./OriginsPanel";

const AUTH_MODES = [
  { value: "qr", label: "QR (telefon)", icon: "tabler:qrcode" },
  { value: "ticket", label: "Ticket (sayt logini)", icon: "tabler:ticket" },
];

// "" is the overview, at the bare /mijoz-tizimlari/<id>.
const TABS = [
  { key: "", label: "Umumiy", icon: "tabler:layout-dashboard" },
  { key: "muhokamalar", label: "Muhokamalar", icon: "tabler:messages" },
  { key: "azolar", label: "Aʼzolar", icon: "tabler:users" },
  { key: "guruhlar", label: "Guruhlar", icon: "tabler:folders" },
  { key: "ulanish", label: "Ulanish", icon: "tabler:plug-connected" },
  { key: "menejerlar", label: "Menejerlar", icon: "tabler:user-shield" },
];

const tabPath = (id, key) => `/mijoz-tizimlari/${id}${key ? `/${key}` : ""}`;

/**
 * One client system, split into tabs (2026-10-22).
 *
 * It used to be one long page, on purpose: a client breaks silently in three
 * ways -- no allowed origin and the widget renders nowhere, no member and
 * nobody can read, no live key and the client system cannot call the API --
 * and a tab you have to click is a failure you have to go looking for. With a
 * thread register added, one page became too long to use, so the three
 * failures moved into a warning strip above the tabs instead: shown on every
 * tab, and only when one is actually true.
 *
 * The tab lives in the URL (/mijoz-tizimlari/<id>/muhokamalar), so a refresh,
 * the back button or a shared link opens the same tab.
 *
 * The counts start from the client payload and are then replaced by what each
 * panel reports when it loads or changes, so a number on screen never
 * disagrees with the table behind it -- even right after adding an origin.
 *
 * Only internal staff reach this panel at all, so the staff-only actions here
 * (credentials, manager grants, switching the client off, the QR / ticket
 * switch) need no extra gate in the UI beyond the one the API enforces anyway.
 * The QR / ticket switch ends every live widget session of the client, so it
 * asks first; the API refuses ticket mode while the client's site sends no
 * full_name, and logs every switch (shown in the switch's tooltip).
 */
const ClientDetailPage = () => {
  const { id, tab = "" } = useParams();
  const [state, setState] = useState({ loading: true, error: "" });
  const [client, setClient] = useState(null);

  const [counts, setCounts] = useState({ origins: null, members: null, credentials: null });
  const [groups, setGroups] = useState([]);
  const [recent, setRecent] = useState(null);

  const [confirmToggle, setConfirmToggle] = useState(false);
  const [toggling, setToggling] = useState(false);

  // Target mode while its confirmation is open, else null.
  const [confirmMode, setConfirmMode] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [lastModeChange, setLastModeChange] = useState(null);

  // Stable identities: each panel takes its reporter as a dependency of the
  // effect that loads it, and a fresh function every render would reload it
  // forever.
  const countOrigins = useCallback((n) => setCounts((c) => ({ ...c, origins: n })), []);
  const countMembers = useCallback((n) => setCounts((c) => ({ ...c, members: n })), []);
  const countCredentials = useCallback(
    (n) => setCounts((c) => ({ ...c, credentials: n })),
    [],
  );
  const onGroupsChange = useCallback((list) => setGroups(list), []);

  useEffect(() => {
    let live = true;
    api
      .get(`/clients/${id}/`)
      .then((res) => {
        if (!live) return;
        setClient(res.data);
        setCounts({
          origins: res.data.origin_count || 0,
          members: res.data.member_count || 0,
          credentials: res.data.credential_count || 0,
        });
        setState({ loading: false, error: "" });
      })
      .catch((err) => {
        if (!live) return;
        setState({
          loading: false,
          error: readApiError(err, {}, "Mijoz tizimini yuklab boʻlmadi."),
        });
      });
    return () => {
      live = false;
    };
  }, [id]);

  // Loaded here rather than only in the Guruhlar tab: the tab count and the
  // members panel's group select both need the tree while that tab is closed.
  useEffect(() => {
    let live = true;
    api
      .get("/groups/", { params: { client: id } })
      .then((res) => live && setGroups(toTree(res.data.results || [])))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [id]);

  useEffect(() => {
    if (tab !== "") return undefined;
    let live = true;
    api
      .get("/threads/", { params: { client: id, limit: 5 } })
      .then((res) => live && setRecent(res.data.results || []))
      .catch(() => live && setRecent([]));
    return () => {
      live = false;
    };
  }, [id, tab]);

  const loadModeHistory = useCallback(() => {
    // Only the tooltip depends on it, so a failure stays silent.
    api
      .get(`/clients/${id}/auth-mode/`)
      .then((res) => setLastModeChange(res.data[0] || null))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    loadModeHistory();
  }, [loadModeHistory]);

  const setAuthMode = async (mode) => {
    setSwitching(true);
    try {
      const res = await api.post(`/clients/${id}/auth-mode/`, { mode });
      setClient(res.data);
      notify.success(
        mode === "ticket"
          ? "Ticket rejimi yoqildi — hamma qaytadan sayt logini bilan kiradi."
          : "QR rejimi yoqildi — hamma qaytadan telefon bilan tasdiqlaydi.",
      );
      setConfirmMode(null);
      loadModeHistory();
    } catch (err) {
      notify.error(readApiError(err, {}, "Kirish usulini oʻzgartirib boʻlmadi."));
    } finally {
      setSwitching(false);
    }
  };

  const setActive = async (active) => {
    setToggling(true);
    try {
      const res = await api.post(`/clients/${id}/${active ? "activate" : "deactivate"}/`);
      setClient(res.data);
      notify.success(
        active
          ? "Mijoz tizimi yoqildi — vidjet va API yana ishlaydi."
          : "Mijoz tizimi oʻchirildi — vidjet va API hamma joyda toʻxtadi.",
      );
      setConfirmToggle(false);
    } catch (err) {
      notify.error(
        readApiError(
          err,
          {},
          active ? "Mijoz tizimini yoqib boʻlmadi." : "Mijoz tizimini oʻchirib boʻlmadi.",
        ),
      );
    } finally {
      setToggling(false);
    }
  };

  if (!TABS.some((t) => t.key === tab)) {
    return <Navigate to={tabPath(id, "")} replace />;
  }

  if (state.loading) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      </div>
    );
  }

  if (state.error || !client) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{state.error || "Topilmadi."}</span>
        </div>
      </div>
    );
  }

  const ticket = client.auth_mode === "ticket";
  // A ticket client adds newcomers to its root group by itself, so an empty
  // member list is not a failure there.
  const problems = client.is_active
    ? [
        counts.origins === 0 && {
          tab: "ulanish",
          icon: "tabler:world-off",
          text: "Ruxsat etilgan manba yoʻq — vidjet hech qaysi saytda koʻrinmaydi.",
          action: "Manba qoʻshish",
        },
        counts.credentials === 0 && {
          tab: "ulanish",
          icon: "tabler:key-off",
          text: "Faol API kaliti yoʻq — tizim muhokama ocha olmaydi.",
          action: "Kalit chiqarish",
        },
        counts.members === 0 &&
          !ticket && {
            tab: "azolar",
            icon: "tabler:users-minus",
            text: "Aʼzo yoʻq — muhokamalarni hech kim oʻqiy olmaydi.",
            action: "Aʼzo qoʻshish",
          },
      ].filter(Boolean)
    : [];

  const tabCount = {
    muhokamalar: { n: client.thread_count || 0 },
    azolar: { n: counts.members, danger: counts.members === 0 && !ticket },
    guruhlar: { n: groups.length },
    ulanish: { danger: counts.origins === 0 || counts.credentials === 0 },
  };

  const stat = (value) => (value === null ? "—" : value);

  return (
    <>
      <div className="ms-crumbs">
        <Link to="/mijoz-tizimlari">Mijoz tizimlari</Link>
        <Icon icon="tabler:chevron-right" />
        <span>{client.name}</span>
      </div>

      <div className="ms-recordhead">
        <div className="ms-recordhead-meta">
          <h1>{client.name}</h1>
          <span className="ms-mono">{client.slug}</span>
        </div>
        <div className="ms-toolbar-spacer" />
        <div
          className="ms-segmented"
          role="group"
          aria-label="Kirish usuli"
          title={
            lastModeChange
              ? `Oxirgi oʻzgarish: ${lastModeChange.by}, ${exactTime(lastModeChange.created_at)}`
              : "Kirish usuli hali oʻzgartirilmagan"
          }
        >
          {AUTH_MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              className={client.auth_mode === m.value ? "is-active" : ""}
              aria-pressed={client.auth_mode === m.value}
              disabled={switching}
              onClick={() => client.auth_mode !== m.value && setConfirmMode(m.value)}
            >
              <Icon icon={m.icon} />
              {m.label}
            </button>
          ))}
        </div>
        <span
          className={`ms-badge ms-badge--${client.is_active ? "success" : "danger"}`}
          title={client.is_active ? "" : exactTime(client.deactivated_at)}
        >
          {client.is_active ? "Faol" : "Oʻchirilgan"}
        </span>
        {client.is_active ? (
          <button
            type="button"
            className="ms-btn ms-btn--ghost ms-btn--sm"
            onClick={() => setConfirmToggle(true)}
          >
            <Icon icon="tabler:plug-off" />
            Oʻchirish
          </button>
        ) : (
          <button
            type="button"
            className="ms-btn ms-btn--primary ms-btn--sm"
            disabled={toggling}
            onClick={() => setActive(true)}
          >
            <Icon icon="tabler:plug" />
            {toggling ? "Yoqilmoqda..." : "Yoqish"}
          </button>
        )}
      </div>

      {(problems.length > 0 || !client.is_active) && (
        <div className="ms-alertbar" role="status">
          {!client.is_active && (
            <div>
              <Icon icon="tabler:plug-off" />
              <span>
                Mijoz tizimi oʻchirilgan ({exactTime(client.deactivated_at)}) — vidjet,
                soketlar va API toʻxtagan. Maʼlumotlar saqlanadi.
              </span>
            </div>
          )}
          {problems.map((p) => (
            <Link key={p.text} to={tabPath(id, p.tab)}>
              <Icon icon={p.icon} />
              <span>{p.text}</span>
              <b>{p.action} →</b>
            </Link>
          ))}
        </div>
      )}

      <nav className="ms-tabs" aria-label="Boʻlimlar">
        {TABS.map((t) => {
          const c = tabCount[t.key];
          return (
            <NavLink
              key={t.key}
              to={tabPath(id, t.key)}
              end
              className={() => `ms-tab${tab === t.key ? " is-active" : ""}`}
            >
              <Icon icon={t.icon} />
              {t.label}
              {c && c.n !== undefined && c.n !== null && (
                <span className={`ms-tab-count${c.danger ? " is-danger" : ""}`}>{c.n}</span>
              )}
              {c && c.n === undefined && c.danger && (
                <span className="ms-tab-count is-danger">!</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {tab === "" && (
        <>
          <div className="ms-stats">
            <Link className="ms-stat" to={tabPath(id, "muhokamalar")}>
              <div className="ms-stat-icon">
                <Icon icon="tabler:messages" />
              </div>
              <div className="ms-stat-body">
                <span>Muhokamalar</span>
                <b>{client.thread_count || 0}</b>
              </div>
            </Link>
            <Link className="ms-stat" to={tabPath(id, "azolar")}>
              <div className={`ms-stat-icon${tabCount.azolar.danger ? " is-warn" : ""}`}>
                <Icon icon="tabler:users" />
              </div>
              <div className="ms-stat-body">
                <span>Aʼzolar</span>
                <b>{stat(counts.members)}</b>
              </div>
            </Link>
            <Link className="ms-stat" to={tabPath(id, "ulanish")}>
              <div className={`ms-stat-icon${counts.origins === 0 ? " is-warn" : ""}`}>
                <Icon icon="tabler:world" />
              </div>
              <div className="ms-stat-body">
                <span>Manbalar</span>
                <b>{stat(counts.origins)}</b>
              </div>
            </Link>
            <Link className="ms-stat" to={tabPath(id, "ulanish")}>
              <div className={`ms-stat-icon${counts.credentials === 0 ? " is-warn" : ""}`}>
                <Icon icon="tabler:key" />
              </div>
              <div className="ms-stat-body">
                <span>Faol kalitlar</span>
                <b>{stat(counts.credentials)}</b>
              </div>
            </Link>
            <Link className="ms-stat" to={tabPath(id, "guruhlar")}>
              <div className="ms-stat-icon">
                <Icon icon="tabler:folders" />
              </div>
              <div className="ms-stat-body">
                <span>Guruhlar</span>
                <b>{groups.length}</b>
              </div>
            </Link>
          </div>

          <div className="ms-recordgrid">
            <div className="ms-panel">
              <div className="ms-panel-head">
                <Icon icon="tabler:info-circle" />
                <h2>Maʼlumot</h2>
              </div>
              <dl className="ms-facts">
                <div>
                  <dt>Nomi</dt>
                  <dd>{client.name}</dd>
                </div>
                <div>
                  <dt>Slug</dt>
                  <dd className="ms-mono">{client.slug}</dd>
                </div>
                <div>
                  <dt>Kirish usuli</dt>
                  <dd>{ticket ? "Ticket (sayt logini)" : "QR (telefon)"}</dd>
                </div>
                <div>
                  <dt>Holati</dt>
                  <dd>
                    <span
                      className={`ms-badge ms-badge--${client.is_active ? "success" : "danger"}`}
                    >
                      {client.is_active ? "Faol" : "Oʻchirilgan"}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Oxirgi faollik</dt>
                  <dd title={exactTime(client.last_activity_at)}>
                    {client.last_activity_at ? relativeTime(client.last_activity_at) : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Yaratilgan</dt>
                  <dd title={exactTime(client.created_at)}>{relativeTime(client.created_at)}</dd>
                </div>
              </dl>
            </div>

            <div className="ms-panel">
              <div className="ms-panel-head">
                <Icon icon="tabler:messages" />
                <h2>Soʻnggi muhokamalar</h2>
                <div className="ms-toolbar-spacer" />
                <Link className="ms-btn ms-btn--ghost ms-btn--sm" to={tabPath(id, "muhokamalar")}>
                  Barchasi
                  <Icon icon="tabler:chevron-right" />
                </Link>
              </div>
              {recent === null ? (
                <div className="ms-state">
                  <div className="ms-spinner" />
                </div>
              ) : recent.length === 0 ? (
                <div className="ms-state">
                  <Icon icon="tabler:message-off" />
                  <span>Hali muhokama yoʻq.</span>
                </div>
              ) : (
                <div className="ms-table-wrap">
                  <table className="ms-table ms-table--compact">
                    <thead>
                      <tr>
                        <th>Sarlavha</th>
                        <th style={{ width: 90 }}>Xabarlar</th>
                        <th style={{ width: 130 }}>Oxirgi xabar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((t) => (
                        <tr key={t.id} className={unseen(t) ? "ms-row-unseen" : undefined}>
                          <td className="ms-td-strong ms-td-clamp">
                            <Link
                              className="ms-link"
                              to={`/muhokamalar/${t.id}`}
                              state={{ from: { clientId: client.id, clientName: client.name } }}
                            >
                              {t.title || t.external_object_id}
                            </Link>
                            <SeenPill row={t} />
                          </td>
                          <td className="ms-td-num">{t.message_count ?? 0}</td>
                          <td className="ms-td-muted" title={exactTime(t.last_message_at)}>
                            {t.last_message_at ? relativeTime(t.last_message_at) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {tab === "muhokamalar" && <ThreadsTable client={client} />}

      {tab === "azolar" && (
        <MembersPanel
          clientId={id}
          clientName={client.name}
          groups={groups}
          onCount={countMembers}
        />
      )}

      {tab === "guruhlar" && <GroupsPanel clientId={id} onGroupsChange={onGroupsChange} />}

      {tab === "ulanish" && (
        <div className="ms-recordgrid ms-recordgrid--half">
          <OriginsPanel clientId={id} onCount={countOrigins} />
          <CredentialsPanel clientId={id} onCount={countCredentials} />
        </div>
      )}

      {tab === "menejerlar" && <ManagersPanel clientId={id} />}

      <Confirm
        open={confirmToggle}
        busy={toggling}
        name={client.name}
        title="Mijoz tizimini oʻchirish"
        message="Vidjet, soketlar va API kirishi bir vaqtda toʻxtaydi — bu tizim ulangan hamma sayt darhol ishlamay qoladi. Hech narsa oʻchirilmaydi: qaytadan yoqilganda hammasi aynan tiklanadi."
        confirmLabel="Oʻchirish"
        cancelLabel="Bekor qilish"
        busyLabel="Oʻchirilmoqda..."
        onConfirm={() => setActive(false)}
        onCancel={() => setConfirmToggle(false)}
      />

      <Confirm
        open={confirmMode !== null}
        busy={switching}
        name={client.name}
        title={confirmMode === "ticket" ? "Ticket rejimiga oʻtish" : "QR rejimiga oʻtish"}
        message={
          confirmMode === "ticket"
            ? "Tizim saytiga kirgan har kim MySuhbatga telefonsiz kiradi; yangi odamlar ildiz guruhga avtomatik qoʻshiladi. Hozir ochiq hamma vidjet sessiyasi tugatiladi — hamma qaytadan kiradi."
            : "Har kim telefondagi MySuhbat ilovasi bilan QR kodni tasdiqlashi kerak boʻladi. Hozir ochiq hamma vidjet sessiyasi tugatiladi — hamma qaytadan kiradi."
        }
        confirmLabel="Oʻtkazish"
        cancelLabel="Bekor qilish"
        busyLabel="Oʻtkazilmoqda..."
        onConfirm={() => setAuthMode(confirmMode)}
        onCancel={() => setConfirmMode(null)}
      />
    </>
  );
};

export default ClientDetailPage;
